import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, authApi, getStoredToken, type BackendProfile } from '@/shared/api';
import { setRealtimeAuth } from '@/shared/db/client';

export type UserRole = BackendProfile['perfil'];
export type Profile = BackendProfile & { displayName: string };

type SessionContextValue = {
  profile: Profile | null;
  loading: boolean;
  signIn: (credentials: { usuarioApp: string; password: string }) => Promise<Profile>;
  signOut: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

function withDisplayName(p: BackendProfile): Profile {
  const composed = `${p.nombre ?? ''} ${p.apellido ?? ''}`.trim();
  return { ...p, displayName: composed || p.usuario_app };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(getStoredToken()));
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    // Page reload path: token survived in localStorage but the Realtime
    // client booted with no auth. Push it now so gallery subscriptions
    // attach as `authenticated` once they mount.
    setRealtimeAuth(token);
    (async () => {
      try {
        const me = await authApi.fetchMe();
        if (!cancelled) setProfile(withDisplayName(me));
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          authApi.logout();
        } else {
          // eslint-disable-next-line no-console
          console.error('[session] fetchMe failed', err);
        }
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (credentials: { usuarioApp: string; password: string }) => {
      const p = await authApi.login(credentials);
      const next = withDisplayName(p);
      setProfile(next);
      return next;
    },
    [],
  );

  const signOut = useCallback(() => {
    authApi.logout();
    setProfile(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(
    () => ({ profile, loading, signIn, signOut }),
    [profile, loading, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function useProfile() {
  return useSession().profile;
}

export function hasRole(profile: Profile | null, roles: UserRole | UserRole[]): boolean {
  if (!profile) return false;
  // Visitante is a universal read-only viewer — they pass every nav
  // / route role allowlist and see the full UI. The backend rejects
  // writes for them (see `requireAuth` in server/src/auth.ts), and the
  // frontend hides edit affordances via `useIsReadOnly`.
  if (profile.perfil === 'visitante') return true;
  const list = Array.isArray(roles) ? roles : [roles];
  return list.includes(profile.perfil);
}

/**
 * `visitante` is a read-only role: backend rejects every non-GET request,
 * frontend should hide edit buttons / dialogs / forms. Use this hook to
 * gate edit affordances. Returns false for any other role (including
 * unauthenticated, since they can't access protected pages).
 */
export function useIsReadOnly(): boolean {
  const perfil = useProfile()?.perfil;
  return perfil === 'visitante' || perfil === 'marketing';
}

export function isReadOnly(profile: Profile | null): boolean {
  return profile?.perfil === 'visitante' || profile?.perfil === 'marketing';
}

/**
 * Ventas write access. Marketing is read-only globally (see `isReadOnly`) with
 * a single carve-out: it can CREATE ventas and act on its OWN (matching the
 * backend guard on the ventas router). These helpers gate the ventas-feature
 * affordances WITHOUT touching `isReadOnly`, so marketing stays read-only in
 * every other module.
 */
export function canCreateVenta(profile: Profile | null): boolean {
  if (!profile) return false;
  if (profile.perfil === 'marketing') return true;
  // Preserve the previous gate for everyone else: non-read-only, and not Nova
  // (Nova doesn't create quotes).
  return !isReadOnly(profile) && profile.perfil !== 'nova';
}
export function useCanCreateVenta(): boolean {
  return canCreateVenta(useProfile());
}

/**
 * Can this user act on (edit / register pago / anular…) a SPECIFIC venta?
 * Marketing may act only on its OWN ventas (`Vendedor_RO === its usuario_app`).
 * Every other role keeps its existing access (admin/administración/ventas/nova
 * → yes; visitante → no) — ventas only ever sees its own rows, so unchanged.
 */
export function canActOnVenta(
  profile: Profile | null,
  vendedorRo: string | null | undefined,
): boolean {
  if (!profile) return false;
  if (profile.perfil === 'marketing') {
    return (vendedorRo ?? '').trim() === (profile.usuario_app ?? '').trim();
  }
  return !isReadOnly(profile);
}
export function useCanActOnVenta(vendedorRo: string | null | undefined): boolean {
  return canActOnVenta(useProfile(), vendedorRo);
}

/**
 * `admin` is the super-admin tier — the only role that should mutate
 * Inventario state (stock buckets, swaps, préstamos, showroom). Use this
 * hook (or its non-hook variant `isSuperAdmin`) to gate Inventario edit
 * affordances.
 *
 * Direct perfil check — does NOT go through `hasRole`, which has a
 * visitante bypass that would otherwise let visitante through this
 * gate (visitante is universally read-only and must NOT see admin
 * edit affordances).
 */
export function useIsSuperAdmin(): boolean {
  return useProfile()?.perfil === 'admin';
}

export function isSuperAdmin(profile: Profile | null): boolean {
  return profile?.perfil === 'admin';
}

/**
 * Admin tier = `admin` + `administracion`. The combo allowed to edit
 * Configuración / ABM master data (artículos, contactos, proveedores,
 * parámetros, etc.). Inventario stays super-admin-only via
 * `useIsSuperAdmin` — admin tier is the broader gate for back-office
 * editing of master records.
 *
 * Visitante is excluded by the direct perfil check.
 */
export function useIsAdminTier(): boolean {
  const perfil = useProfile()?.perfil;
  return perfil === 'admin' || perfil === 'administracion';
}

export function isAdminTier(profile: Profile | null): boolean {
  return profile?.perfil === 'admin' || profile?.perfil === 'administracion';
}

/**
 * Best-fit landing route for a profile. Used by ProtectedRoute when a
 * user hits a route they're not allowed on, and by the post-login
 * redirect. Mirrors the AppShell nav: mantenimiento has no Inicio so
 * they go to /mantenimiento; everyone else lands on /.
 */
export function defaultLandingForProfile(profile: Profile | null): string {
  if (!profile) return '/login';
  if (profile.perfil === 'mantenimiento') return '/mantenimiento';
  if (profile.perfil === 'nova') return '/administracion';
  return '/';
}
